"use client";

import SidebarCard from "@/containers/components/cards/sidebarCard/SidebarCard";
import SubmitCard from "@/containers/components/cards/SubmitCard";
import useSessionStorage from "@/shared/hooks/useSessionStorage";
import { jalaliToGregorian } from "@/shared/libs/date";
import { hotelGTM } from "@/shared/libs/gtm";
import { generateRoomIds } from "@/shared/libs/utils";
import type { THotelConfirmSchema } from "@/shared/schemas/hotel/hotelConfirm.schema";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import { addDays } from "date-fns";
import { format } from "date-fns-jalali";
import { useRouter } from "next/navigation";
import StickyBox from "react-sticky-box";
import { HotelDetailFilter } from "./HotelDetailFilter";
import staticData from "@/public/data/hotel/detail.json";
import Flex from "@/shared/components/common/Flex";
import {
  createSearchParams,
  createUrlWithSearchParams,
} from "@/shared/libs/url-utils";
import { useMemo, useState } from "react";
import type { HotelDetail } from "@/types/services/hotel/apis/Detail";
import { useDeviceType } from "@/shared/context/DeviceContext";
import type { HotelDetailParams } from "../../HotelDetailBody";

interface SidebarProps {
  params: HotelDetailParams;
  searchParams: SearchParams;
  stayNights: number;
  accommodation_key: string;
  hotelDetail: HotelDetail;
  roomAvailable: boolean;
}

export default function Sidebar(props: SidebarProps) {
  const {
    searchParams,
    params,
    stayNights,
    accommodation_key,
    hotelDetail,
    roomAvailable,
  } = props;

  const { city, slug } = params;
  const { checkIn, checkOut } = searchParams;
  const { rooms, setDetailRooms } = useHotelStore((state) => state);
  const today = new Date();
  const tommorow = addDays(today, 1);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { images, name, address, stars } = hotelDetail ?? {};

  const totalPrice = useMemo(
    () =>
      rooms.reduce((prev, curr) => {
        return prev + curr.rate_plans.price * curr.count;
      }, 0),
    [rooms],
  );

  const [hotelInfo, SetHotelInfo] = useSessionStorage<
    THotelConfirmSchema | string
  >("hotelInfo");
  const [currentHotel, setCurrentHotel] = useSessionStorage("hotel");

  if (currentHotel !== slug) {
    setCurrentHotel(slug);
    setDetailRooms([]);
  }

  if (hotelInfo) SetHotelInfo("");

  const sidebarPropsData = {
    checkin: format(
      checkIn ? jalaliToGregorian(checkIn) : today,
      "EEEE d MMMM yyyy",
    ),
    checkout: format(
      checkOut ? jalaliToGregorian(checkOut) : tommorow,
      "EEEE d MMMM yyyy",
    ),
    img: images?.find((img) => img.type === "thumbnail")?.path ?? "",
    rooms: rooms ?? [],
    stayNight: stayNights,
    title: name,
    description: address,
    stars,
    roomAvailable,
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (rooms) {
      const params = [
        {
          name: "rooms",
          value: searchParams.rooms ?? "2",
        },
        {
          name: "room_id",
          value: generateRoomIds(rooms),
        },
        {
          name: "hotel",
          value: slug,
        },
        {
          name: "accommodationKey",
          value: accommodation_key,
        },
      ];

      hotelGTM.selectProduct({
        event: "selectRoom",
        checkIn,
        checkOut,
        hotelName: slug,
        location: city,
        price: totalPrice,
        roomType: "Single",
      });
      new Promise(() => {
        router.push(
          createUrlWithSearchParams(
            "/hotel/${city}/passengers",
            createSearchParams(new URLSearchParams(searchParams), params),
          ),
        );
      }).then(() => {
        setIsLoading(false);
      });
    }
  };

  return useDeviceType() === "mobile" ? (
    <SubmitCard
      content={<Flex>{""}</Flex>}
      isLoading={isLoading}
      buttonText={
        totalPrice === 0
          ? staticData.sidebarCard.footer.noRoomSelectedMobile
          : null
      }
      totalPrice={totalPrice}
      onSubmit={handleSubmit}
    />
  ) : (
    <StickyBox offsetTop={80} offsetBottom={20}>
      {checkIn && (
        <SidebarCard sidebarData={sidebarPropsData} onSubmit={handleSubmit} />
      )}
      <HotelDetailFilter {...props} />
    </StickyBox>
  );
}
